import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ModuleMilestone } from '@/components/university/ModuleMilestone';
import SEO from '@/components/common/SEO';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const M = '#06060c';
const D = '#0a0a14';
const G = '#d4a843';
const IN = '#0ea5e9';
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface Lesson {
  lesson_id: number;
  title: string;
  core_concepts: string[];
  passing_score: number;
  learning_objective: string;
}

interface Module {
  module_id: number;
  title: string;
  certificate: string;
  lessons: Lesson[];
}

interface DegreeClass {
  id: string;
  name: string;
  module_plan: Module[];
}

interface ProgressState {
  current_unit: number;
  current_lesson: number;
  total_lessons_completed: number;
}

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
        style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: G }}
      />
    </div>
  );
}

export default function UniversityModulePage() {
  // Route: /university/module/:classId/:moduleId
  const { classId, moduleId } = useParams<{ classId: string; moduleId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();

  const [degree, setDegree] = useState<DegreeClass | null>(null);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [mastery, setMastery] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneResolved, setMilestoneResolved] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || !classId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data: cls } = await supabase
          .from('classes')
          .select('id, name, module_plan')
          .eq('id', classId)
          .single();

        if (cancelled) return;
        setDegree(cls as DegreeClass);

        const { data: enrol } = await supabase
          .from('class_enrollments')
          .select('mastery_score, progress_state')
          .eq('student_id', user!.id)
          .eq('class_id', classId)
          .maybeSingle();

        if (cancelled) return;
        if (enrol) {
          setMastery(Number(enrol.mastery_score) || 0);
          setProgress(enrol.progress_state as ProgressState);
        }
      } catch (err) {
        console.error('ModulePage load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user, classId]);

  if (authLoading || loading) return <Loader />;
  if (!degree) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M, color: MU }}>
      {t('university.module.notFound', 'Module not found.')}
    </div>
  );

  const modId = parseInt(moduleId || '1');
  const mod = degree.module_plan.find(m => m.module_id === modId);
  if (!mod) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M, color: MU }}>
      {t('university.module.notFound', 'Module not found.')}
    </div>
  );

  const currentUnit = progress?.current_unit || 1;
  const currentLesson = progress?.current_lesson || 1;
  const modLocked = modId > currentUnit;
  const modCompleted = modId < currentUnit;

  function lessonStatus(lessonId: number): 'completed' | 'current' | 'locked' {
    if (modId < currentUnit) return 'completed';
    if (modId > currentUnit) return 'locked';
    if (lessonId < currentLesson) return 'completed';
    if (lessonId === currentLesson) return 'current';
    return 'locked';
  }

  const completedCount = mod.lessons.filter(l => lessonStatus(l.lesson_id) === 'completed').length;
  const progressPct = Math.min(Math.round((completedCount / mod.lessons.length) * 100), 100);
  const moduleComplete = modCompleted || completedCount === mod.lessons.length;

  useEffect(() => {
    if (moduleComplete && !milestoneResolved && loading === false) {
      // Check if this is a "fresh" completion or if the user already saw it
      const milestoneKey = `milestone_seen_${classId}_${modId}`;
      const alreadySeen = localStorage.getItem(milestoneKey);
      if (!alreadySeen) {
        setShowMilestone(true);
        localStorage.setItem(milestoneKey, 'true');
      }
      setMilestoneResolved(true);
    }
  }, [moduleComplete, milestoneResolved, loading, classId, modId]);

  // Sibling modules for nav
  const prevMod = degree.module_plan.find(m => m.module_id === modId - 1);
  const nextMod = degree.module_plan.find(m => m.module_id === modId + 1);

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO title={mod?.title || "Module"} noindex />
      <ModuleMilestone
        isOpen={showMilestone}
        onClose={() => setShowMilestone(false)}
        moduleTitle={mod.title}
        degreeName={degree.name}
      />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: moduleComplete
          ? 'linear-gradient(160deg, rgba(74,222,128,0.08) 0%, rgba(212,168,67,0.06) 100%)'
          : modLocked
            ? 'rgba(255,255,255,0.02)'
            : 'linear-gradient(160deg, rgba(14,165,233,0.12) 0%, rgba(212,168,67,0.06) 100%)',
        borderBottom: `1px solid ${BR}`,
      }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: isMobile ? '32px 20px' : '44px 24px 36px' }}>

          {/* Breadcrumb */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: isMobile ? 16 : 20,
            fontSize: 12,
            color: MU,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', padding: 0, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              Dashboard
            </button>
            <span>•</span>
            <button
              onClick={() => navigate(`/degree/${classId}`)}
              style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', padding: 0, fontSize: 13, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Degree
            </button>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 24 : 20
          }}>
            <div style={{ flex: 1 }}>
              {/* Module number pill */}
              <div style={{ marginBottom: 12 }}>
                <span style={{
                  background: modLocked ? 'rgba(255,255,255,0.06)' : moduleComplete ? 'rgba(74,222,128,0.15)' : 'rgba(14,165,233,0.15)',
                  color: modLocked ? MU : moduleComplete ? '#4ade80' : IN,
                  border: `1px solid ${modLocked ? BR : moduleComplete ? 'rgba(74,222,128,0.3)' : 'rgba(14,165,233,0.3)'}`,
                  borderRadius: 100, padding: '5px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
                }}>
                  {modLocked ? `🔒 LOCKED` : moduleComplete ? `✓ COMPLETE` : `▶ IN PROGRESS`}
                </span>
              </div>

              <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                {mod.title}
              </h1>
              <p style={{ color: MU, fontSize: 13, margin: 0, fontWeight: 500 }}>
                {degree.name.replace('BSc ', '')} · {mod.lessons.length} lessons
              </p>
            </div>

            {/* Start/Continue CTA */}
            {!modLocked && (
              <motion.button
                whileHover={{ scale: 1.04, boxShadow: '0 8px 24px rgba(212,168,67,0.3)' }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate(`/learn/${classId}?module=${modId}&lesson=${currentUnit === modId ? currentLesson : 1}`)}
                style={{
                  background: `linear-gradient(135deg, ${G}, #b8912e)`,
                  color: '#06060c', border: 'none', borderRadius: 12,
                  padding: '14px 32px', fontSize: 15, fontWeight: 800,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {completedCount === 0
                  ? 'Start Module →'
                  : moduleComplete
                    ? 'Review Module →'
                    : 'Continue →'}
              </motion.button>
            )}
          </div>

          {/* Progress bar */}
          {!modLocked && (
            <div style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                <span style={{ color: MU, fontWeight: 600 }}>
                  {completedCount}/{mod.lessons.length} LESSONS COMPLETE
                </span>
                <span style={{ color: progressPct > 0 ? G : MU, fontWeight: 800 }}>{progressPct}%</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 100, height: 7, overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    height: '100%', borderRadius: 100,
                    background: moduleComplete ? '#4ade80' : `linear-gradient(90deg, ${IN}, ${G})`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── LESSONS LIST ─────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 820, margin: '0 auto', padding: isMobile ? '24px 16px 80px' : '40px 24px 80px' }}>

        {/* Certificate earned banner */}
        <AnimatePresence>
          {moduleComplete && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'rgba(212,168,67,0.08)',
                border: '1.5px solid rgba(212,168,67,0.3)',
                borderRadius: 20, padding: isMobile ? '20px' : '20px 24px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 16,
                marginBottom: 32,
              }}
            >
              <span style={{ fontSize: 32 }}>🏆</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: G, marginBottom: 4, letterSpacing: '0.5px' }}>
                  CERTIFICATE EARNED
                </div>
                <div style={{ fontSize: 14, color: TX, fontWeight: 600 }}>
                  {mod.certificate}
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.04 }}
                onClick={() => navigate('/certificates')}
                style={{
                  marginLeft: isMobile ? 0 : 'auto',
                  background: G, color: '#06060c',
                  border: 'none', borderRadius: 10, padding: '10px 20px',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                View Certificate →
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Locked warning */}
        {modLocked && (
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`,
            borderRadius: 14, padding: '20px 24px', marginBottom: 28,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 28 }}>🔒</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                {t('university.module.lockedTitle', 'Module Locked')}
              </div>
              <div style={{ fontSize: 13, color: MU }}>
                {t('university.module.lockedHint', 'Complete the previous module to unlock this one.')}
              </div>
            </div>
          </div>
        )}

        {/* Lessons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
          {mod.lessons.map((lesson, i) => {
            const status = lessonStatus(lesson.lesson_id);
            const isClickable = status !== 'locked';

            return (
              <motion.div
                key={lesson.lesson_id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                whileHover={isClickable && !isMobile ? { borderColor: 'rgba(212,168,67,0.4)', y: -2 } : {}}
                onClick={() => isClickable && navigate(`/learn/${classId}?module=${modId}&lesson=${lesson.lesson_id}`)}
                style={{
                  background: status === 'current' ? 'rgba(212,168,67,0.07)' : CA,
                  border: `1.5px solid ${status === 'current' ? 'rgba(212,168,67,0.4)' : BR}`,
                  borderRadius: 20, padding: isMobile ? '20px' : '20px 24px',
                  cursor: isClickable ? 'pointer' : 'default',
                  opacity: status === 'locked' ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'flex-start',
                  gap: isMobile ? 12 : 20
                }}>

                  {/* Status indicator row for mobile */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: status === 'completed'
                        ? 'rgba(74,222,128,0.15)'
                        : status === 'current'
                          ? 'rgba(212,168,67,0.15)'
                          : 'rgba(255,255,255,0.06)',
                      border: `1.5px solid ${status === 'completed' ? '#4ade80' : status === 'current' ? G : BR}`,
                      fontSize: 15, fontWeight: 700,
                      color: status === 'completed' ? '#4ade80' : status === 'current' ? G : TX
                    }}>
                      {status === 'completed' ? '✓' : status === 'current' ? '▶' : lesson.lesson_id}
                    </div>

                    {isMobile && (
                      <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: status === 'current' ? G : TX }}>
                        {lesson.title}
                      </h3>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    {!isMobile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 5 }}>
                        <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: status === 'current' ? G : TX }}>
                          Lesson {lesson.lesson_id}: {lesson.title}
                        </h3>
                        {status === 'current' && (
                          <span style={{
                            background: G, color: '#06060c',
                            borderRadius: 8, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                          }}>
                            CURRENT
                          </span>
                        )}
                      </div>
                    )}

                    <p style={{ color: MU, fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>
                      {lesson.learning_objective}
                    </p>

                    {/* Core concepts chips */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: isMobile ? 20 : 0 }}>
                      {lesson.core_concepts.map(concept => (
                        <span key={concept} style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: `1px solid ${BR}`,
                          borderRadius: 7, padding: '3px 10px',
                          fontSize: 11, color: MU, fontWeight: 500
                        }}>
                          {concept}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Pass score & action */}
                  <div style={{
                    flexShrink: 0,
                    textAlign: isMobile ? 'left' : 'right',
                    width: isMobile ? '100%' : 'auto',
                    borderTop: isMobile ? `1px solid ${BR}` : 'none',
                    paddingTop: isMobile ? 16 : 0,
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'center' : 'flex-end',
                    gap: 12
                  }}>
                    <div style={{ fontSize: 11, color: MU, fontWeight: 600 }}>
                      PASS: {lesson.passing_score}%
                    </div>
                    {isClickable && (
                      <motion.button
                        whileHover={{ scale: 1.06 }}
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/learn/${classId}?module=${modId}&lesson=${lesson.lesson_id}`);
                        }}
                        style={{
                          background: status === 'completed'
                            ? 'rgba(74,222,128,0.12)'
                            : 'rgba(212,168,67,0.12)',
                          color: status === 'completed' ? '#4ade80' : G,
                          border: `1px solid ${status === 'completed' ? 'rgba(74,222,128,0.3)' : 'rgba(212,168,67,0.3)'}`,
                          borderRadius: 10, padding: '8px 18px',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        {status === 'completed' ? 'Review →' : 'Start →'}
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Certificate info card */}
        <div style={{
          background: 'rgba(14,165,233,0.06)', border: `1px solid rgba(14,165,233,0.2)`,
          borderRadius: 16, padding: '22px 24px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 36,
        }}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>🏆</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 4 }}>
              {t('university.module.certTitle', 'Complete this module to earn:')}
            </div>
            <div style={{ fontSize: 14, color: IN }}>{mod.certificate}</div>
            <div style={{ fontSize: 12, color: MU, marginTop: 4 }}>
              {t('university.module.certReq', 'Pass all lessons with {{score}}%+ to unlock your PDF certificate.', { score: 70 })}
            </div>
          </div>
        </div>

        {/* Module navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          {prevMod ? (
            <motion.button
              whileHover={{ x: -3 }}
              onClick={() => navigate(`/module/${classId}/${prevMod.module_id}`)}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 12, padding: '12px 20px',
                cursor: 'pointer', color: TX, fontSize: 14, fontWeight: 600,
              }}
            >
              ← {prevMod.title}
            </motion.button>
          ) : <div />}

          {nextMod && !modLocked ? (
            <motion.button
              whileHover={{ x: 3 }}
              onClick={() => navigate(`/module/${classId}/${nextMod.module_id}`)}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 12, padding: '12px 20px',
                cursor: 'pointer', color: TX, fontSize: 14, fontWeight: 600,
              }}
            >
              {nextMod.title} →
            </motion.button>
          ) : <div />}
        </div>
      </div>
    </div>
  );
}
